import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useParams } from 'react-router-dom';
import { initPostHog } from './lib/posthog';
import { initWebVitals } from './reportWebVitals';
import './env'; // Initialize and validate environment variables
import './utils/testPostHog'; // Load PostHog test utility
import './utils/envDebug'; // Load environment debug utility
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { UserFlowProvider } from './contexts/UserFlowContext';
import ThemeToggle from './components/ThemeToggle';
import StepperNavigation from './components/StepperNavigation';
import Button from './components/ui/Button';
import { Menu, ChevronDown } from 'lucide-react';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import UpgradeSuccessToast from './components/UpgradeSuccessToast';
import BetaGate from './components/BetaGate';
import BetaBanner from './components/BetaBanner';
import BetaGateWrapper from './components/BetaGateWrapper';
import OfflineBanner from './components/OfflineBanner';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { useDataManager } from './hooks/useDataManager';
import AuthDebugPanel from './components/AuthDebugPanel';
import AuthDebugBanner from './components/AuthDebugBanner';
import FirstTimeOnboarding from './components/FirstTimeOnboarding';
import VersionInfo from './components/VersionInfo';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import PageSkeleton from './components/PageSkeleton';
import Status from './pages/Status';

// Lazy load heavy components for better performance
const LoginMagic = React.lazy(() => import('./pages/LoginMagic'));
const LoginSuccess = React.lazy(() => import('./pages/LoginSuccess'));
const DebtTracker = React.lazy(() => import('./pages/MyDebtsPage'));
// Removed legacy AICoach - now using Coach.jsx
const MoneyMakeoverArticle = React.lazy(() => import('./pages/MoneyMakeoverArticle'));
const PlanPage = React.lazy(() => import('./pages/PlanPage'));
const Article = React.lazy(() => import('./pages/Article'));
const Coach = React.lazy(() => import('./pages/Coach'));

const Waitlist = React.lazy(() => import('./pages/Waitlist'));
const Strategies = React.lazy(() => import('./pages/Strategies'));
const Security = React.lazy(() => import('./pages/Security'));
const HowItWorks = React.lazy(() => import('./pages/HowItWorks'));

// New library structure - lazy load all library pages
const LibraryLayout = React.lazy(() => import('./pages/library/LibraryLayout'));
const LibraryIndexPage = React.lazy(() => import('./pages/library'));
const DebtSnowballVsAvalanche = React.lazy(() => import('./pages/library/DebtSnowballVsAvalanche'));
const SmallDebtChallenge = React.lazy(() => import('./pages/library/SmallDebtChallenge'));
const AICoachLibrary = React.lazy(() => import('./pages/library/AICoach'));
const PayOff5000 = React.lazy(() => import('./pages/library/PayOff5000'));
const SpendReviewSnoop = React.lazy(() => import('./pages/library/SpendReviewSnoop'));
const FiveDebtMistakes = React.lazy(() => import('./pages/library/FiveDebtMistakes'));
const Upgrade = React.lazy(() => import('./pages/Upgrade'));
const Billing = React.lazy(() => import('./pages/Billing'));
const MyPlan = React.lazy(() => import('./pages/MyPlan'));
const Profile = React.lazy(() => import('./pages/Profile'));


// Article redirect component
function ArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/library/${slug}`} replace />;
}

// const user = true;

// Dropdown Component
function Dropdown({ title, items, isOpen, setIsOpen, colors }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        size="sm"
        className={`${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`}
      >
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>
      
      {isOpen && (
        <div className={`absolute top-full left-0 mt-1 w-56 ${colors.surface} ${colors.border} border rounded-lg shadow-lg z-50`}>
          <div className="py-1">
            {items.map(([path, label, description]) => (
              <Link
                key={path}
                to={path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 text-sm transition-colors hover:${colors.surfaceSecondary} ${colors.text.primary}`}
              >
                <div className="font-medium">{label}</div>
                {description && <div className={`text-xs ${colors.text.muted} mt-1`}>{description}</div>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const location = useLocation();
  const { colors } = useTheme();
  const { user, authReady } = useAuth();
  const { debts } = useDataManager();
  const hasDebtData = debts && debts.length > 0;

  // Check if user needs onboarding
  const isFirstTimeUser = !localStorage.getItem('onboarding_completed');
  const shouldShowOnboarding = isFirstTimeUser && authReady && location.pathname === '/';
  
  // Debug logging for onboarding (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 [Onboarding Debug]', {
      isFirstTimeUser,
      authReady: !authReady,
      pathname: location.pathname,
      shouldShowOnboarding,
      onboardingCompleted: localStorage.getItem('onboarding_completed')
    });
  }

  if (!authReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  // Show onboarding for first-time users on homepage
  if (shouldShowOnboarding) {
    return <FirstTimeOnboarding onComplete={() => window.location.href = '/debts?onboarding=completed'} />;
  }

  // Core Navigation - always visible
  const coreNavItems = [
    ['/', 'Home'],
    ['/my-plan', 'My Progress'],
    ['/upgrade', 'Pricing'],
  ];


  // Tools & Support dropdown
  const toolsItems = [
    ['/debts', 'Manage Debts', 'Track and organize your debts'],
    ['/plan', 'Debt Plan', 'View payoff scenarios and timelines'],
  ];

  // Resources dropdown
  const resourcesItems = [
    ['/library', 'Library', 'Articles, guides & help resources'],
  ];

  // Account dropdown
  const accountItems = [
    ['/profile', 'My Profile', 'Account settings & data management'],
    ['/billing', 'Billing', 'Manage subscription & payment settings'],
  ];

  return (
    <div className={`min-h-screen ${colors.background}`}>
      <AuthDebugBanner />
      <BetaBanner />
      {/* Enhanced Navigation */}
      <nav className={`${colors.surface} shadow-sm ${colors.border} border-b sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo/Brand */}
            <Link 
              to="/"
              className="flex items-center space-x-3 transition-colors"
            >
              <img 
                src="/logo-transparent.png" 
                alt="Try Snowball logo" 
                className="h-10 w-auto"
              />
              <span className={`text-base font-semibold ${colors.text.primary}`}>TrySnowball</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {/* Core Navigation */}
              {coreNavItems.map(([path, label]) => (
                <Link
                  key={path}
                  to={path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === path
                      ? 'bg-primary text-white shadow-sm transform scale-105'
                      : `${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`
                  }`}
                >
                  {label}
                </Link>
              ))}
              
              {/* Divider */}
              <div className={`w-px h-6 mx-2 ${colors.border}`}></div>
              
              {/* Direct AI Coach Link - DISABLED DURING AUTH REBUILD */}
              {/*
              <Link
                to="/ai/coach"
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === '/ai/coach'
                    ? `${colors.text.primary} ${colors.surfaceSecondary}`
                    : `${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`
                } rounded-md`}
              >
                🤖 AI Coach
              </Link>
              */}
              <Dropdown
                title="🛠️ Tools"
                items={toolsItems}
                isOpen={toolsOpen}
                setIsOpen={setToolsOpen}
                colors={colors}
              />
              <Dropdown
                title="📚 Resources"
                items={resourcesItems}
                isOpen={resourcesOpen}
                setIsOpen={setResourcesOpen}
                colors={colors}
              />
              {user ? (
                <Dropdown
                  title="⚙️ Account"
                  items={accountItems}
                  isOpen={accountOpen}
                  setIsOpen={setAccountOpen}
                  colors={colors}
                />
              ) : (
                <Link to="/auth/login">
                  <Button variant="primary" size="sm">
                    Login / Sign Up
                  </Button>
                </Link>
              )}
              
              <ThemeToggle />
            </div>

            {/* Mobile menu button & theme toggle */}
            <div className="lg:hidden flex items-center space-x-2">
              <ThemeToggle />
              <Button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                variant="ghost"
                size="sm"
                className={`${colors.text.secondary} hover:${colors.text.primary} p-2`}
                leftIcon={Menu}
              >
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className={`lg:hidden ${colors.border} border-t py-2`}>
              <div className="flex flex-col space-y-1">
                {/* Core Navigation */}
                {coreNavItems.map(([path, label]) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => {
                      setIsMenuOpen(false);
                    }}
                    className={`px-4 py-2 text-left rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === path
                        ? 'bg-primary text-white'
                        : `${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`
                    }`}
                  >
                    {label}
                  </Link>
                ))}
                
                {/* Divider */}
                <div className={`my-2 border-t ${colors.border}`}></div>
                
                {/* AI Coach Link - DISABLED DURING AUTH REBUILD */}
                {/*
                <Link
                  to="/ai/coach"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-2 text-left rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/ai/coach'
                      ? `${colors.text.primary} ${colors.surfaceSecondary}`
                      : `${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`
                  }`}
                >
                  🤖 AI Coach
                </Link>
                */}
                
                {/* Tools Section */}
                <div className={`px-4 py-1 text-xs font-semibold ${colors.text.muted} uppercase tracking-wider mt-2`}>
                  🛠️ Tools & Support
                </div>
                {toolsItems.map(([path, label]) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-2 pl-8 text-left rounded-lg text-sm font-medium transition-colors ${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`}
                  >
                    {label}
                  </Link>
                ))}
                
                {/* Resources Section */}
                <div className={`px-4 py-1 text-xs font-semibold ${colors.text.muted} uppercase tracking-wider mt-2`}>
                  📚 Resources
                </div>
                {resourcesItems.map(([path, label]) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-2 pl-8 text-left rounded-lg text-sm font-medium transition-colors ${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`}
                  >
                    {label}
                  </Link>
                ))}
                
                {/* Account Section */}
                {user ? (
                  <>
                    <div className={`px-4 py-1 text-xs font-semibold ${colors.text.muted} uppercase tracking-wider mt-2`}>
                      ⚙️ Account & Settings
                    </div>
                    {accountItems.map(([path, label]) => (
                      <Link
                        key={path}
                        to={path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`px-4 py-2 pl-8 text-left rounded-lg text-sm font-medium transition-colors ${colors.text.secondary} hover:${colors.text.primary} hover:${colors.surfaceSecondary}`}
                      >
                        {label}
                      </Link>
                    ))}
                  </>
                ) : (
                  <div className="mx-4 mt-4">
                    <Link to="/auth/login" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="primary" size="sm" className="w-full">
                        Login / Sign Up
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Stepper Navigation */}
      <StepperNavigation hasDebtData={hasDebtData} />

      {/* Auth Debug Panel (Development Only) */}
      <AuthDebugPanel />

      {/* Page Content */}
      <div className={`transition-all duration-300 ease-in-out ${colors.background} min-h-screen`}>
        <UpgradeSuccessToast />
        <Routes>
  {/* 🏠 Core Navigation Routes */}
  <Route path="/" element={<Home />} />
  <Route path="/waitlist" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Waitlist />
    </React.Suspense>
  } />
  <Route path="/upgrade" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Upgrade />
    </React.Suspense>
  } />
  <Route path="/pricing" element={<Navigate to="/upgrade" replace />} />
  <Route path="/my-plan" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <MyPlan />
    </React.Suspense>
  } />
  
  {/* 🤖 AI Tools Routes - DISABLED DURING AUTH REBUILD */}
  {/*
  <Route path="/ai/coach" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Coach />
    </React.Suspense>
  } />
  <Route path="/ai/report" element={<Navigate to="/ai/coach" replace />} />
  */}
  
  {/* 🛠️ Tools & Support Routes */}
  <Route path="/debts" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <BetaGate><DebtTracker /></BetaGate>
    </React.Suspense>
  } />
  <Route path="/plan" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <BetaGate><PlanPage /></BetaGate>
    </React.Suspense>
  } />
  <Route path="/plan/scenario" element={<Navigate to="/plan?scenario=true" replace />} />
  
  {/* 📚 Resources Routes - New nested library structure */}
  <Route path="/library" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <LibraryLayout />
    </React.Suspense>
  }>
    <Route index element={
      <React.Suspense fallback={<PageSkeleton />}>
        <LibraryIndexPage />
      </React.Suspense>
    } />
    <Route path="debt-snowball-vs-avalanche" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <DebtSnowballVsAvalanche />
      </React.Suspense>
    } />
    <Route path="small-debt-challenge" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <SmallDebtChallenge />
      </React.Suspense>
    } />
    <Route path="ai-coach" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <AICoachLibrary />
      </React.Suspense>
    } />
    <Route path="pay-off-5000-fast" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <PayOff5000 />
      </React.Suspense>
    } />
    <Route path="spend-review-snoop" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <SpendReviewSnoop />
      </React.Suspense>
    } />
    <Route path="five-debt-mistakes" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <FiveDebtMistakes />
      </React.Suspense>
    } />
    <Route path="strategies" element={
      <React.Suspense fallback={<PageSkeleton />}>
        <Strategies />
      </React.Suspense>
    } />
  </Route>
  
  {/* Legacy library routes for backwards compatibility */}
  <Route path="/library/money-makeover" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <MoneyMakeoverArticle />
    </React.Suspense>
  } />
  <Route path="/library/:slug" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Article />
    </React.Suspense>
  } />
  <Route path="/how-it-works" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <HowItWorks />
    </React.Suspense>
  } />
  <Route path="/security" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Security />
    </React.Suspense>
  } />
  
  {/* 👤 Auth Routes */}
  <Route path="/auth/login" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <LoginMagic />
    </React.Suspense>
  } />
  <Route path="/login-success" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <LoginSuccess />
    </React.Suspense>
  } />
  
  {/* Account Routes */}
  <Route path="/profile" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Profile />
    </React.Suspense>
  } />
  <Route path="/billing" element={
    <React.Suspense fallback={<PageSkeleton />}>
      <Billing />
    </React.Suspense>
  } />
  
  {/* System Status Page */}
  <Route path="/status" element={<Status />} />
  
  {/* Referral system */}
  <Route path="/ref/:referralId" element={<Navigate to="/" replace />} />
  
  {/* Legacy redirects for SEO/bookmarks */}
  <Route path="/money-makeover" element={<Navigate to="/library/money-makeover" replace />} />
  <Route path="/ai-coach" element={<Navigate to="/ai/coach" replace />} />
  <Route path="/coach" element={<Navigate to="/ai/coach" replace />} />
  <Route path="/ai-report" element={<Navigate to="/ai/coach" replace />} />
  
  {/* Old external GPT coach redirect - now points to unified coach */}
  <Route path="/external-coach" element={<Navigate to="/ai/coach" replace />} />
  <Route path="/article/:slug" element={<ArticleRedirect />} />
  <Route path="/what-if" element={<Navigate to="/plan/scenario" replace />} />
  <Route path="/login" element={<Navigate to="/auth/login" replace />} />
  <Route path="/login/success" element={<Navigate to="/login-success" replace />} />
  <Route path="/auth/success" element={<Navigate to="/login-success" replace />} />
  
  {/* Catch-all 404 route - must be last */}
  <Route path="*" element={<NotFound />} />
</Routes>
      </div>

      {/* Footer */}
      <footer className={`${colors.surface} border-t ${colors.border} mt-auto`}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand & Description */}
            <div className="col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/logo-transparent.png" 
                  alt="Try Snowball logo" 
                  className="h-8 w-auto"
                />
                <span className={`text-lg font-semibold ${colors.text.primary}`}>TrySnowball</span>
              </div>
              <p className={`text-sm ${colors.text.secondary} max-w-sm`}>
                Your AI debt coach helping UK users achieve financial freedom through smart debt payoff strategies.
              </p>
            </div>

            {/* Quick Links */}
            <div className="col-span-1">
              <h3 className={`text-sm font-semibold ${colors.text.primary} mb-4`}>Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/debts" className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors`}>
                    Manage Debts
                  </Link>
                </li>
                <li>
                  <Link to="/ai/coach" className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors`}>
                    AI Coach
                  </Link>
                </li>
                <li>
                  <Link to="/library" className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors`}>
                    Library
                  </Link>
                </li>
              </ul>
            </div>

            {/* Trust & Support */}
            <div className="col-span-1">
              <h3 className={`text-sm font-semibold ${colors.text.primary} mb-4`}>Trust & Support</h3>
              <ul className="space-y-2 mb-4">
                <li>
                  <Link 
                    to="/security" 
                    onClick={() => {
                      if (window.analytics?.track) {
                        window.analytics.track('footer_link_clicked', { link: 'security' });
                      }
                    }}
                    className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors`}
                  >
                    Security
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/how-it-works" 
                    onClick={() => {
                      if (window.analytics?.track) {
                        window.analytics.track('footer_link_clicked', { link: 'how_it_works' });
                      }
                    }}
                    className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors`}
                  >
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/library/strategies" 
                    onClick={() => {
                      if (window.analytics?.track) {
                        window.analytics.track('footer_link_clicked', { link: 'strategies' });
                      }
                    }}
                    className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors`}
                  >
                    Strategies
                  </Link>
                </li>
              </ul>
              <div className="space-y-2">
                <a 
                  href="mailto:hello@trysnowball.co.uk" 
                  onClick={() => {
                    // Track support email clicks
                    if (window.analytics?.track) {
                      window.analytics.track('support_email_clicked', {
                        location: 'footer',
                        email: 'hello@trysnowball.co.uk'
                      });
                    }
                  }}
                  className={`text-sm ${colors.text.secondary} hover:${colors.text.primary} transition-colors flex items-center space-x-2`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span>hello@trysnowball.co.uk</span>
                </a>
                <p className={`text-xs ${colors.text.muted}`}>
                  Questions? Feedback? We'd love to hear from you!
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={`border-t ${colors.border} pt-6 mt-6 flex flex-col sm:flex-row justify-between items-center`}>
            <div className="flex items-center space-x-4">
              <p className={`text-xs ${colors.text.muted}`}>
                © {new Date().getFullYear()} TrySnowball. Your financial data stays private and secure.
              </p>
              <VersionInfo />
            </div>
            <div className="flex space-x-4 mt-4 sm:mt-0">
              <span className={`text-xs ${colors.text.muted}`}>Made with ❤️ for debt freedom</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  useEffect(() => {
    // Initialize PostHog analytics
    initPostHog();
    
    // Initialize Web Vitals monitoring
    initWebVitals();
    
    // Load security dev tools in development
    if (process.env.NODE_ENV === 'development') {
      import('./utils/securityDevTools').catch(() => {
        // Ignore if security dev tools fail to load
      });
    }
  }, []);

  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <UserFlowProvider>
          <AuthProvider>
            <BetaGateWrapper>
              <Router>
                <OfflineBanner />
                <Navigation />
                {/* <GPTDebugPanel /> Debug panel - remove in production */}
              </Router>
            </BetaGateWrapper>
          </AuthProvider>
        </UserFlowProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;